/* eslint-disable @typescript-eslint/camelcase */
import { Checkbox, Grid, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import Button from 'components/Button';
import Loading from 'components/Loading';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import { getObjectsAvailableRequest } from 'modules/permission/actions';
import {
  ListObjectOperatorDataProps as ListObjectProps,
  ObjectOperatorProps as ObjectProps,
} from 'modules/permission/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState, { ListDataProps } from 'store/interfaces';
import { useTableFilters } from 'hooks/useTableFilters';
import { initialQuery } from '..';
import styles from '../../Template/styles';
import ActionSearch from '../ActionSearch';
import { ENTITY_NAME } from './Content';

const useStyles = makeStyles(styles);

interface Props {
  id: string;
  items: {
    loading: boolean;
    selected: ListObjectProps;
    available: ListObjectProps;
  };
  pending: ObjectProps[];
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: (values: ObjectProps[]) => void;
  getObjectsAvailableRequest: (props: {
    role_uuid: string;
    entity: string;
    query?: any;
  }) => Promise<void>;
}

const ListAvailable = ({
  id,
  items,
  pending,
  isLoading,
  onCancel,
  onConfirm,
  getObjectsAvailableRequest,
}: Props) => {
  const classes = useStyles();
  const {
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
  } = useTableFilters(initialQuery);

  const [data, setData] = React.useState<ListDataProps | null>(null);
  const [checked, setChecked] = React.useState<string[]>(
    pending.map(({ uuid }) => uuid)
  );
  const [pendingList, setPendingList] = React.useState<ObjectProps[]>(pending);

  const handleCheck = {
    exists: (item: ObjectProps) => checked.includes(item?.uuid),
    toggle: (item: ObjectProps) => {
      if (pendingList.find(i => i.uuid === item?.uuid)) {
        setChecked(prev => prev.filter(i => i !== item?.uuid));
        setPendingList(prev => prev.filter(i => i.uuid !== item?.uuid));
      } else {
        setChecked(prev => [...prev, item?.uuid]);
        setPendingList(prev => [...prev, item]);
      }
    },
    toggleAll: (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setChecked(data?.results.map(({ uuid }) => uuid) || []);
        setPendingList(data?.results || []);
      } else {
        setChecked([]);
        setPendingList([]);
      }
    },
  };

  const handleConfirm = () => onConfirm(pendingList);

  React.useEffect(() => {
    getObjectsAvailableRequest({ role_uuid: id, entity: ENTITY_NAME, query });
  }, [getObjectsAvailableRequest, id, query]);

  React.useEffect(() => {
    if (items?.available) {
      setData(items.available);
    }
  }, [items]);

  if (!data) {
    return <Loading />;
  }

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <ActionSearch
          id={`search-${ENTITY_NAME}`}
          label={i18n.t('SEARCH_OPERATOR_KEY')}
          onSearch={handleSearch}
          isLoading={items?.loading || isLoading}
        />
      </Grid>
      <br />
      <Grid className={classes.minimalTable}>
        <Grid className={classes.table}>
          <TableItems
            overrideRowsCount={10}
            header={
              <>
                <TableCell className={classes.checkboxCell}>
                  <Checkbox onChange={handleCheck.toggleAll} />
                </TableCell>
                <TableCell
                  order
                  className={classes.header}
                  handleOrder={() => handleOrder('first_name')}
                >
                  {i18n.t('OPERATOR_NAME_KEY')}
                </TableCell>
                <TableCell
                  order
                  className={classes.header}
                  handleOrder={() => handleOrder('email')}
                >
                  {i18n.t('EMAIL_KEY')}
                </TableCell>
                <TableCell
                  order
                  className={classes.header}
                  handleOrder={() => handleOrder('company__company_name')}
                >
                  {i18n.t('COMPANIES_KEY')}
                </TableCell>
              </>
            }
            renderItems={(item: ObjectProps) => (
              <TableRow
                key={item?.uuid}
                className={clsx({
                  [classes.pending]: handleCheck.exists(item),
                })}
              >
                <TableCell
                  className={classes.checkboxCell}
                  style={{ width: 20 }}
                >
                  <Checkbox
                    onChange={() => handleCheck.toggle(item)}
                    checked={handleCheck.exists(item)}
                  />
                </TableCell>
                <TableCell>
                  {`${item?.first_name} ${item?.last_name}`}
                </TableCell>
                <TableCell>{item?.email}</TableCell>
                <TableCell>{item?.company?.name}</TableCell>
              </TableRow>
            )}
            data={items.available}
            isLoading={items?.loading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('NO_OPERATOR_KEY')}
            colCount={4}
          />
        </Grid>

        <Grid container justify="flex-end" className={classes.buttonsBase}>
          <Grid container className={classes.buttonsAlign}>
            <Button submitButton id="submit-button" onClick={handleConfirm}>
              {i18n.t('ADD_KEY')}
            </Button>
            <Button id="cancel-button" variant="outlined" onClick={onCancel}>
              {i18n.t('CANCEL_KEY')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  permission: { objects, isLoading },
}: ApplicationState) => ({
  items: objects[ENTITY_NAME],
  isLoading,
});

export default connect(mapStateToProps, {
  getObjectsAvailableRequest,
})(ListAvailable);
